import { useEffect, useState } from "react";
import { readGeocodeCache, writeGeocodeCache } from "../helper/geocodeCache";

// Resolve a map point for a company location (FE #751).
//
// Most ZoomInfo-sourced locations carry an address but no lat/long. When the
// coordinates are missing we geocode the address once per mount through the
// Maps JS key's Geocoding service, check the per-user cache first, and fall
// back to "unavailable" when there is no address, no Maps library, or no result.
// Billing: one Geocoding request per uncached view (free to 10k/month).

// The query Google sees. Built from real field values only: the display
// helpers interpolate missing fields as the literal string "undefined".
export function geocodableAddress(location) {
  const full = String(location?.fullAddress ?? "").trim();
  if (full) return full;
  return [location?.fullStreet, location?.city, location?.region1, location?.postalCode, location?.countryId]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

function storedPoint(location) {
  if (location?.lat == null || location?.long == null) return null;
  return { lat: location.lat, lng: location.long };
}

function initialState(location, address) {
  const point = storedPoint(location);
  if (point) return { status: "ready", point };
  if (!String(address || "").trim()) return { status: "unavailable" };
  const cached = readGeocodeCache(address);
  if (cached) return { status: "ready", point: cached };
  if (typeof window.google?.maps?.importLibrary !== "function") return { status: "unavailable" };
  return { status: "loading" };
}

async function geocodeAddress(address) {
  const { Geocoder } = await window.google.maps.importLibrary("geocoding");
  const response = await new Geocoder().geocode({ address });
  const location = response?.results?.[0]?.geometry?.location;
  if (!location) return null;
  return { lat: location.lat(), lng: location.lng() };
}

export default function useGeocodedLocation(location, address) {
  const inputs = `${location?.lat}|${location?.long}|${address}`;
  const [state, setState] = useState(() => ({ inputs, ...initialState(location, address) }));
  let current = state;
  if (state.inputs !== inputs) {
    // A reused card was handed another company: start over for the new inputs.
    current = { inputs, ...initialState(location, address) };
    setState(current);
  }
  const needsGeocode = current.status === "loading";

  useEffect(() => {
    if (!needsGeocode) return undefined;
    let cancelled = false;
    const settle = (next) => {
      if (!cancelled) setState({ inputs, ...next });
    };
    geocodeAddress(address)
      .then((point) => {
        if (cancelled) return;
        if (!point) {
          settle({ status: "unavailable" });
          return;
        }
        writeGeocodeCache(address, point);
        settle({ status: "ready", point });
      })
      .catch(() => settle({ status: "unavailable" }));
    return () => {
      cancelled = true;
    };
  }, [needsGeocode, address, inputs]);

  return current.point ? { status: current.status, point: current.point } : { status: current.status };
}
